import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/lib/supabase";
import type { PermissionMap, ResourcePermissions } from "@/lib/admin-api/departments";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Access level for a single resource (mirrors the DB department permissions) */
export type { ResourcePermissions } from "@/lib/admin-api/departments";

export interface AdminUserProfile {
  readonly id: string;
  readonly fullName: string;
  readonly email: string;
  readonly role: string;
  readonly departmentName: string | null;
  readonly permissions: PermissionMap;
  readonly isActive: boolean;
}

interface AdminAuthContextValue {
  /** The authenticated admin user, or null while loading / when unauthenticated */
  readonly user: AdminUserProfile | null;
  /** True while the initial session check is in progress */
  readonly isLoading: boolean;
  /**
   * Check whether the current user can access a given resource.
   *
   * - If no `resource` is provided the check always passes (item has no restriction).
   * - If no `action` is provided it defaults to `"read"`.
   * - `super_admin` / `Direction` department always have full access.
   */
  canAccess(resource?: string, action?: keyof ResourcePermissions): boolean;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AdminAuthProviderProps {
  readonly children: ReactNode;
}

export function AdminAuthProvider({ children }: AdminAuthProviderProps) {
  const [user, setUser] = useState<AdminUserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch the admin user profile + department permissions from Supabase
  const fetchAdminProfile = useCallback(async () => {
    try {
      const {
        data: { user: authUser },
        error: authError,
      } = await supabase.auth.getUser();

      if (!authUser || authError) {
        setUser(null);
        return;
      }

      // Join admin_users → departments to get department name + permissions JSONB
      // Direction department has cross-platform access (cieda + djayma)
      const { data: adminRow, error: adminError } = await supabase
        .from("admin_users")
        .select(
          "id, full_name, email, role, is_active, platform, department_id, departments(name, permissions)"
        )
        .eq("id", authUser.id)
        .eq("is_active", true)
        .single();

      if (adminError || !adminRow) {
        setUser(null);
        return;
      }

      // Verify platform access: djayma platform or Direction department
      const dept = adminRow.departments as unknown as
        | { name: string; permissions: PermissionMap }
        | null;
      const isDjayma = adminRow.platform === "djayma";
      const isDirection = dept?.name === "Direction";
      if (!isDjayma && !isDirection) {
        setUser(null);
        return;
      }

      setUser({
        id: adminRow.id,
        fullName: adminRow.full_name,
        email: adminRow.email,
        role: adminRow.role,
        departmentName: dept?.name ?? null,
        permissions: dept?.permissions ?? {},
        isActive: adminRow.is_active,
      });
    } catch {
      // Network or unexpected error — treat as unauthenticated
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdminProfile();

    // Re-check when auth state changes (login/logout).
    // Skip INITIAL_SESSION — the fetchAdminProfile() above handles the first load.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "INITIAL_SESSION") return;
      if (session) {
        fetchAdminProfile();
      } else {
        setUser(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchAdminProfile]);

  // Memoised canAccess predicate
  const canAccess = useCallback(
    (resource?: string, action: keyof ResourcePermissions = "read"): boolean => {
      // No resource restriction means always visible
      if (!resource) return true;

      // Still loading — optimistically show items to prevent flicker
      if (!user) return isLoading;

      // Super-admin or Direction department: unrestricted access
      if (user.role === "super_admin" || user.departmentName === "Direction") {
        return true;
      }

      const resourcePerms: ResourcePermissions | undefined =
        user.permissions[resource];

      // If the resource is not in the permission map, deny access
      if (!resourcePerms) return false;

      return resourcePerms[action] === true;
    },
    [user, isLoading],
  );

  const value = useMemo<AdminAuthContextValue>(
    () => ({ user, isLoading, canAccess }),
    [user, isLoading, canAccess],
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the admin auth context.
 *
 * Must be used inside `<AdminAuthProvider>`.
 */
export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used within an <AdminAuthProvider>");
  }
  return ctx;
}
